/**
 * NewProgramModal — create a program (Projects → Programs → Portfolios).
 * Mirrors NewPortfolioModal; adds an optional parent-portfolio picker.
 */
import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@/components/ui/Modal';
import { Input, Select, Textarea, Label } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { useProjectStore } from '@/store/useProjectStore';
import { toast } from '@/store/useToastStore';
import type { Program, ProgramStatus } from '@/types';

interface NewProgramModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pre-select a parent portfolio (e.g. when created from a portfolio page). */
  defaultPortfolioId?: string | null;
}

/** PROG-XXXXXX (6 alphanumerics). */
function generateProgramId(): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let id = 'PROG-';
  for (let i = 0; i < 6; i += 1) id += alphabet[Math.floor(Math.random() * alphabet.length)];
  return id;
}

export function NewProgramModal({ isOpen, onClose, defaultPortfolioId = null }: NewProgramModalProps) {
  const navigate = useNavigate();
  const addProgram = useProjectStore((s) => s.addProgram);
  const portfolios = useProjectStore((s) => s.portfolios);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [manager, setManager] = useState('');
  const [status, setStatus] = useState<ProgramStatus>('active');
  const [portfolioId, setPortfolioId] = useState<string>('');
  const [targetBudget, setTargetBudget] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setDescription('');
    setManager('');
    setStatus('active');
    setPortfolioId(defaultPortfolioId ?? '');
    setTargetBudget('');
    setSubmitting(false);
  }, [isOpen, defaultPortfolioId]);

  const trimmedName = name.trim();
  const nameValid = trimmedName.length > 0 && trimmedName.length <= 100;
  const budgetValid = targetBudget === '' || (Number(targetBudget) >= 0 && !Number.isNaN(Number(targetBudget)));
  const formValid = nameValid && budgetValid && !submitting;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!formValid) return;
    setSubmitting(true);
    const newProgram: Program = {
      id: generateProgramId(),
      name: trimmedName,
      description: description.trim(),
      manager: manager.trim(),
      status,
      startDate: null,
      endDate: null,
      targetBudget: targetBudget === '' ? 0 : Number(targetBudget),
      portfolioId: portfolioId || null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    addProgram(newProgram);
    toast.success(`Program "${newProgram.name}" created`);
    onClose();
    navigate(`/programs/${newProgram.id}`);
  };

  const footer = (
    <>
      <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
      <Button variant="accent" onClick={handleSubmit} disabled={!formValid} isLoading={submitting}>Create program</Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="New program" size="lg" footer={footer}>
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div>
          <Label htmlFor="npg-name" required>Program name</Label>
          <Input id="npg-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Core Banking Modernization" autoFocus maxLength={100} disabled={submitting} />
          {name.length > 0 && !nameValid && <div className="text-xs text-danger-dark mt-1">Name must be 1–100 characters</div>}
        </div>

        <div>
          <Label htmlFor="npg-description">Description</Label>
          <Textarea id="npg-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What is this program about?" rows={2} disabled={submitting} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="npg-manager">Manager</Label>
            <Input id="npg-manager" value={manager} onChange={(e) => setManager(e.target.value)} placeholder="e.g. Jane Doe, Program Manager" disabled={submitting} />
          </div>
          <div>
            <Label htmlFor="npg-status">Status</Label>
            <Select id="npg-status" value={status} onChange={(e) => setStatus(e.target.value as ProgramStatus)} disabled={submitting}>
              <option value="active">Active</option>
              <option value="on-hold">On hold</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="npg-portfolio">Parent portfolio</Label>
            <Select id="npg-portfolio" value={portfolioId} onChange={(e) => setPortfolioId(e.target.value)} disabled={submitting}>
              <option value="">Standalone (no portfolio)</option>
              {portfolios.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </div>
          <div>
            <Label htmlFor="npg-budget">Target budget (USD)</Label>
            <Input id="npg-budget" type="number" min={0} step={10000} value={targetBudget} onChange={(e) => setTargetBudget(e.target.value)} placeholder="0" disabled={submitting} />
            {targetBudget !== '' && !budgetValid && <div className="text-xs text-danger-dark mt-1">Budget must be a non-negative number</div>}
          </div>
        </div>

        <p className="text-xs text-ink-tertiary pt-2 border-t border-line-light">
          You can add projects to the program after creating it. Health, dates, completion, and budget roll up
          automatically from the projects you assign. A program can belong to a portfolio, or stand alone.
        </p>
      </form>
    </Modal>
  );
}
